import { useSelector, useDispatch } from 'react-redux';
import { MdExpandLess, MdExpandMore, MdClose } from 'react-icons/md';
import XCircularProgress from './common/XCircularProgress';
import XButton from './common/XButton';
import { toggleUploadWidget, removeUpload, clearCompletedUploads } from '../redux/actions/fileUpload';

const widgetClasses = `fixed bottom-18.25 right-6
  w-80 max-w-80 max-h-150
  bg-surface rounded-t shadow-modal
  z-[1055] flex flex-col
  overflow-hidden box-border
  transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
  max-md:w-[calc(100%-32px)] max-md:right-4 max-md:left-4 max-md:bottom-17.5`;

const widgetMinimizedClasses = 'max-h-17';

const headerClasses = `flex justify-between items-center p-2.5
  bg-primary hover:bg-primary-hover
  cursor-pointer select-none
  transition-all duration-200 box-border w-full`;

const titleClasses = 'flex items-center gap-2.5 font-semibold text-sm text-white tracking-[calc(var(--spacing)*0.075)]';

const controlsClasses = 'flex items-center gap-3';

const clearBtnClasses = `px-2! py-1!
  bg-white/20! border! border-white/40! rounded-md
  text-white text-[calc(var(--spacing)*2.5)] font-semibold
  cursor-pointer transition-all duration-200
  uppercase tracking-[calc(var(--spacing)*0.125)]
  hover:bg-white/30! hover:border-white/60! hover:-translate-y-px`;

const toggleBtnClasses = `bg-transparent border-0 text-white
  cursor-pointer transition-all duration-200 rounded-md
  flex items-center justify-center
  hover:bg-white/20`;

const contentClasses = `upload-widget-content-scroll
  max-h-130 overflow-y-auto overflow-x-hidden
  p-1.5 bg-surface-light box-border w-full`;

const itemClasses = `group relative bg-white border border-border-light
  rounded-lg px-2 py-1.5 mb-0.5 last:mb-0
  transition-all duration-200
  shadow-[0px_1px_2px_rgba(16,24,40,0.08)]
  box-border w-full
  hover:border-primary hover:shadow-[0px_2px_8px_rgba(0,111,129,0.12)]`;

const closeBtnClasses = `absolute top-0.5 right-0.5
  bg-transparent border-0 text-border
  cursor-pointer p-0.5 rounded-sm
  flex items-center justify-center
  transition-all duration-200 z-1
  opacity-0 group-hover:opacity-100
  hover:bg-danger/10 hover:text-danger`;

const itemMainClasses = `grid grid-cols-[1fr_65px_25px] items-center gap-4
  w-full box-border pr-4.5`;

const fileNameClasses = `text-xs text-content font-semibold
  overflow-hidden text-ellipsis whitespace-nowrap leading-tight
  [grid-column:1]`;

const statusTextClasses = `text-[calc(var(--spacing)*2.75)] font-semibold text-subtle
  tracking-[calc(var(--spacing)*0.05)] leading-tight whitespace-nowrap text-right [grid-column:2]`;

const circularProgressClasses = `leading-none [grid-column:3] justify-self-end
  max-md:w-10 max-md:h-10`;

const errorClasses = `mt-1 text-[calc(var(--spacing)*2.5)] text-danger leading-snug font-medium
  px-2 py-1 bg-danger/5 rounded
  border-l-2 border-danger break-words max-w-full`;

const STATUS_LABELS = {
    uploading: 'Uploading',
    completed: 'Complete',
    failed: 'Failed',
    queued: 'Queued',
};

const getStatusText = (status) => STATUS_LABELS[status] ?? STATUS_LABELS.queued;

const isWidgetUpload = (upload) => !upload.hasInlineProgress;

const UploadProgressWidget = () => {
    const dispatch = useDispatch();
    const {
        uploadQueue,
        activeUploads,
        completedUploads,
        failedUploads,
        widgetMinimized,
        widgetVisible,
    } = useSelector(state => state.fileUpload);

    const activeUploadsArray = Object.values(activeUploads).filter(isWidgetUpload);
    const queuedUploads = uploadQueue.filter(isWidgetUpload);
    const widgetCompletedUploads = completedUploads.filter(isWidgetUpload);
    const widgetFailedUploads = failedUploads.filter(isWidgetUpload);
    const totalUploads = queuedUploads.length + activeUploadsArray.length + widgetCompletedUploads.length + widgetFailedUploads.length;

    if (!widgetVisible || totalUploads === 0) {
        return null;
    }

    const handleToggle = () => dispatch(toggleUploadWidget());
    const handleRemove = (uploadId) => dispatch(removeUpload(uploadId));
    const handleClearCompleted = () => dispatch(clearCompletedUploads());

    const renderUploadItem = (upload) => {
        const isCompleted = upload.status === 'completed';
        const isFailed = upload.status === 'failed';
        const isQueued = upload.status === 'queued' || !upload.status;

        const progress = isQueued ? 0 : (isCompleted ? 100 : (upload.progress || 0));
        const status = upload.status || 'queued';
        const statusText = getStatusText(status);

        return (
            <div key={upload.id} className={itemClasses}>
                {(isCompleted || isFailed) && (
                    <XButton
                        variant="close"
                        className={closeBtnClasses}
                        onClick={() => handleRemove(upload.id)}
                        title="Remove"
                        aria-label="Remove upload"
                    >
                        <MdClose size={14} />
                    </XButton>
                )}
                <div className={itemMainClasses}>
                    <span className={fileNameClasses} title={upload.fileName}>
                        {upload.fileName}
                    </span>
                    <span className={statusTextClasses}>
                        {statusText}
                    </span>
                    <XCircularProgress
                        value={progress}
                        isFailed={isFailed}
                        size={25}
                        fontSize="24px"
                        className={circularProgressClasses}
                    />
                </div>
                {isFailed && upload.error && (
                    <div className={errorClasses}>{upload.error}</div>
                )}
            </div>
        );
    };

    return (
        <div className={`${widgetClasses} ${widgetMinimized ? widgetMinimizedClasses : ''}`}>
            <div className={headerClasses} onClick={handleToggle}>
                <div className={titleClasses}>
                    <span>
                        {activeUploadsArray.length > 0
                            ? `Uploading ${activeUploadsArray.length} file${activeUploadsArray.length > 1 ? 's' : ''}`
                            : `${totalUploads} upload${totalUploads > 1 ? 's' : ''}`}
                    </span>
                </div>
                <div className={controlsClasses}>
                    {widgetCompletedUploads.length > 0 && !widgetMinimized && (
                        <XButton
                            variant="tab"
                            className={clearBtnClasses}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClearCompleted();
                            }}
                            title="Clear completed"
                        >
                            Clear
                        </XButton>
                    )}
                    <XButton
                        variant="close"
                        className={toggleBtnClasses}
                        title={widgetMinimized ? 'Expand' : 'Minimize'}
                        aria-label={widgetMinimized ? 'Expand' : 'Minimize'}
                    >
                        {widgetMinimized ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
                    </XButton>
                </div>
            </div>

            {!widgetMinimized && (
                <div className={contentClasses}>
                    {activeUploadsArray.map(renderUploadItem)}
                    {queuedUploads.map(file => renderUploadItem({ ...file, status: 'queued' }))}
                    {widgetFailedUploads.map(renderUploadItem)}
                    {widgetCompletedUploads.map(renderUploadItem)}
                </div>
            )}
        </div>
    );
};

export default UploadProgressWidget;
